export const SITE_PRIVACY_MD = `**Effective date: September 13, 2026**
**Last updated: September 13, 2026**

Build Make Win Ventures LLC (“Build Make Win,” “we,” “us,” or “our”) respects your privacy. This Privacy Policy explains how we collect, use, disclose, and protect personal information when you visit **buildmakewin.com** or contact us through the website (collectively, the “Site”).

## 1. Scope

This Privacy Policy applies only to the Build.Make.Win corporate website at **buildmakewin.com**.

Build Make Win owns and develops products, including CoachER. A Build Make Win product may have its own privacy policy that applies to information collected through that product. Information collected through CoachER is governed by the CoachER Privacy Policy available at **buildmakewin.com/coacher/privacy**, not this Privacy Policy.

## 2. Information We Collect

The Site is primarily an informational website. We may collect the following categories of personal information.

### Information You Provide

If you email us or use a contact form on the Site, we may collect:

- Your name;
- Your email address;
- Your company or organization, if you provide it;
- The contents of your message; and
- Any other information you choose to provide.

Please do not send sensitive personal information, confidential business information, or information about children through the Site’s general contact channel.

### Information Collected Automatically

When you visit the Site, our hosting, security, and infrastructure providers may automatically process limited technical information needed to deliver and protect the Site, such as:

- Internet Protocol address;
- Browser and device type;
- Operating system;
- Pages requested and referring page;
- Date and time of access; and
- Diagnostic, security, and error-log information.

We do not use the Build.Make.Win Site to create user accounts, process purchases or subscriptions, or collect payment-card information.

## 3. Cookies and Online Tracking

The Site may use cookies, local storage, or similar technologies that are strictly necessary for security, network management, accessibility, or basic website operation. Our hosting and infrastructure providers may also use these technologies to provide their services.

As of the effective date of this Privacy Policy, we do not use advertising pixels, targeted-advertising cookies, or technologies designed to track your activity over time across unrelated websites for advertising purposes.

Some browsers offer a “Do Not Track” setting. Because there is no uniform standard for responding to legacy Do Not Track signals, the Site does not respond differently to those signals. We do not sell personal information or share it for cross-context behavioral advertising, regardless of whether a signal is enabled. Where applicable law requires recognition of another browser-based opt-out preference signal, we will honor it as required.

## 4. How We Use Information

We may use personal information to:

- Operate, maintain, secure, and improve the Site;
- Respond to questions, messages, and business inquiries;
- Diagnose technical issues and prevent fraud, abuse, or security incidents;
- Protect our rights, property, users, and the public;
- Comply with legal obligations and enforce our agreements; and
- Evaluate or complete a corporate transaction, such as a financing, merger, acquisition, reorganization, or sale of assets.

We do not use information collected through the Site to make decisions that produce legal or similarly significant effects about individuals.

## 5. How We Disclose Information

We may disclose personal information in the following circumstances:

- **Service providers:** We may disclose information to vendors that host, secure, maintain, or support the Site and our business communications. They may process information only to perform services for us and subject to appropriate obligations.
- **Professional advisers:** We may disclose information to attorneys, accountants, insurers, auditors, or other professional advisers when reasonably necessary.
- **Legal and safety reasons:** We may disclose information if we reasonably believe it is necessary to comply with law or legal process, respond to lawful requests, protect rights, safety, or property, investigate fraud or security concerns, or enforce our terms.
- **Business transfers:** Information may be disclosed or transferred as part of a merger, financing, acquisition, reorganization, bankruptcy, sale of assets, or similar transaction, subject to applicable law.
- **At your direction:** We may disclose information when you request or consent to the disclosure.

We do not sell personal information for money. We do not share personal information for cross-context behavioral advertising or use it for targeted advertising.

## 6. Data Retention

We retain personal information only for as long as reasonably necessary for the purposes described in this Privacy Policy, including to respond to inquiries, maintain appropriate business records, secure the Site, resolve disputes, and meet legal obligations.

Retention periods vary depending on the type of information and why it was collected. For example, correspondence may be kept while an inquiry or business relationship remains active and for a reasonable period afterward. Technical logs are generally retained for shorter periods unless they are needed to investigate a security incident or comply with law.

When information is no longer needed, we will delete it or de-identify it where reasonably practicable, subject to lawful backup and recordkeeping requirements.

## 7. Data Security

We use reasonable administrative, technical, and organizational safeguards designed to protect personal information. No website, network, or storage system is completely secure, and we cannot guarantee absolute security.

## 8. Your Privacy Choices and Rights

You may choose not to provide personal information, although that may prevent us from responding to your request.

Depending on where you live and subject to applicable exceptions, you may have rights to request that we:

- Confirm whether we process your personal information;
- Provide access to or a copy of personal information we maintain about you;
- Correct inaccurate personal information;
- Delete personal information;
- Restrict or object to certain processing; or
- Provide information in a portable format.

The Site does not sell personal information or use it for targeted advertising, so there is no sale or targeted-advertising activity to opt out of.

To submit a privacy request, email **support@buildmakewin.com** with the subject line “Privacy Request.” Please describe your request and the email address or interaction to which it relates.

We may need to verify your identity before completing a request. You may use an authorized agent where permitted by law, but we may require proof of the agent’s authority and verification of your identity.

We will not discriminate against you for exercising an applicable privacy right. If applicable law gives you the right to appeal our decision, you may appeal by replying to our response and stating that you are requesting an appeal.

## 9. Children’s Privacy

The Site is intended for a general audience and is not directed to children under 13. We do not knowingly collect personal information from children under 13 through the Site.

If you believe a child has provided personal information through the Site, contact us at **support@buildmakewin.com** so we can review and delete it where appropriate.

This section applies to the Build.Make.Win corporate Site only. Any youth-related information processed through CoachER is addressed in the separate CoachER Privacy Policy.

## 10. Third-Party Websites and Products

The Site may link to third-party websites, app stores, social platforms, or Build Make Win products that operate under separate privacy practices.

We are not responsible for the privacy practices of third parties. Review the privacy policy of each service you use.

## 11. International Visitors

Build Make Win is based in Ohio, United States. If you visit the Site from outside the United States, your information may be processed in the United States or other locations where our service providers operate.

Those locations may have data-protection laws different from those in your jurisdiction. Where required, we rely on an appropriate legal basis and safeguards for processing or transferring personal information.

## 12. Changes to This Privacy Policy

We may update this Privacy Policy from time to time. We will post the updated version on this page and revise the “Last updated” date above.

If changes materially affect how we use personal information, we will provide additional notice when required by law.

## 13. Contact Us

For questions about this Privacy Policy or our privacy practices, contact:

**Build Make Win Ventures LLC**
Ohio, United States
**Email:** support@buildmakewin.com
`;
